import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import Header from "../components/Header";
import Sidebar from "../components/Sidebar";
import RequestCard from "../components/RequestCard";
import { Button } from "../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import {
  PlusCircle, Filter, Star, CheckCircle2, Clock,
  Inbox, ArrowRight, TrendingUp, RotateCcw, X,
  Camera, DollarSign,
} from "lucide-react";

// Phase d'avancement dérivée de l'état mission serveur (source de vérité)
const MISSION_PHASE = {
  AWAITING_PICKUP: 0, LOADED: 1,
  DELIVERY_PENDING_CONFIRMATION: 2, DELIVERED: 2, SETTLED: 2,
  DISPUTED: 2, DISPUTE_ESCALATED: 2, DISPUTE_RESOLVED: 2,
};
const missionPhase = (req) => MISSION_PHASE[req.mission_state] ?? 0;

// ── Active séquestre card (client) ────────────────────────────────
function SequestCard({ req }) {
  // v2.0 : le statut serveur fait foi (in_progress = séquestre complet)
  if (req.status !== "in_progress") return null;

  const acceptedOffer = req.offers?.find(o => o.status === "accepted");
  const amount = acceptedOffer?.price_eur || 0;
  const phase  = missionPhase(req);
  const pct    = phase === 0 ? 33 : phase === 1 ? 66 : 100;

  return (
    <Link to={`/requests/${req.request_id}`} className="block">
      <div className="bg-slate-900 rounded-xl p-5 relative overflow-hidden hover:ring-2 hover:ring-orange-500 transition-all">
        <div className="absolute -top-8 -right-8 w-32 h-32 rounded-full bg-orange-500/10" />
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-orange-500 animate-pulse" />
            <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">
              SÉQUESTRE · {req.request_id?.slice(-5).toUpperCase()}
            </span>
          </div>
          <span className="text-[11px] font-bold text-orange-400 bg-orange-500/10 px-2.5 py-1 rounded-full">
            {phase === 0 ? "Prise en charge" : phase === 1 ? "En transit" : "Livré"}
          </span>
        </div>
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <span className="font-display font-extrabold text-white text-2xl">{req.pickup?.city}</span>
            <ArrowRight className="w-5 h-5 text-orange-500" />
            <span className="font-display font-extrabold text-white text-2xl">{req.dropoff?.city}</span>
          </div>
          {amount > 0 && (
            <span className="font-display font-extrabold text-2xl text-orange-400">{amount.toFixed(0)} €</span>
          )}
        </div>
        <div className="h-1.5 bg-white/10 rounded-full overflow-hidden">
          <div className="h-full bg-orange-500 rounded-full transition-all duration-700" style={{ width: `${pct}%` }} />
        </div>
      </div>
    </Link>
  );
}

// ── Mission row (liste publiées) ──────────────────────────────────
function MissionRow({ req }) {
  const cfg = {
    open:      { label: "PUBLIÉE",     cls: "text-emerald-600 bg-emerald-50 border-emerald-200" },
    offered:   { label: "OFFRE REÇUE", cls: "text-orange-600 bg-orange-50 border-orange-200"   },
    accepted:  { label: "ACCEPTÉE",    cls: "text-blue-600 bg-blue-50 border-blue-200"          },
    completed: { label: "TERMINÉE",    cls: "text-slate-500 bg-slate-100 border-slate-200"      },
    cancelled: { label: "ANNULÉE",     cls: "text-red-500 bg-red-50 border-red-200"             },
  }[req.status] || { label: req.status, cls: "text-slate-500 bg-slate-100 border-slate-200" };

  return (
    <Link to={`/requests/${req.request_id}`}
      className="flex items-center justify-between py-3 px-1 hover:bg-slate-50 rounded-lg transition-colors group">
      <div className="min-w-0">
        <div className="flex items-center gap-2 font-display font-bold text-slate-900 text-sm group-hover:text-orange-600 transition-colors">
          {req.pickup?.city} <ArrowRight className="w-3.5 h-3.5 text-slate-400" /> {req.dropoff?.city}
        </div>
        <div className="text-xs text-slate-400 mt-0.5">
          {Math.round(req.distance_km)} km · {req.vehicle_type}
          {req.budget_eur ? ` · ${req.budget_eur} €` : ""}
        </div>
      </div>
      <span className={`text-[11px] font-bold px-2.5 py-1 rounded-full border flex-shrink-0 ml-3 ${cfg.cls}`}>
        {cfg.label}
      </span>
    </Link>
  );
}

// ── ACTION REQUISE card (pro) ─────────────────────────────────────
function ActionCard({ req }) {
  const acceptedOffer = req.offers?.find(o => o.status === "accepted");
  const amount  = acceptedOffer?.price_eur || 0;
  const fee     = Math.round(amount * 0.10);
  const proNet  = amount - fee;

  return (
    <Link to={`/requests/${req.request_id}`} className="block">
      <div className="border-2 border-orange-400 rounded-xl p-4 bg-white hover:bg-orange-50/30 transition-colors">
        <div className="flex items-start justify-between gap-3 mb-3">
          <div>
            <div className="text-[10px] uppercase tracking-widest font-bold text-orange-600 mb-1">
              ACTION REQUISE
            </div>
            <div className="font-display font-bold text-slate-900 flex items-center gap-2">
              {req.pickup?.city} <ArrowRight className="w-4 h-4 text-orange-500" /> {req.dropoff?.city}
            </div>
            <div className="text-xs text-slate-500 mt-1">
              {Math.round(req.distance_km)} km · {req.vehicle_type} · {req.client_name}
            </div>
          </div>
          <div className="text-right flex-shrink-0">
            <div className="font-display font-extrabold text-2xl text-orange-500">{proNet} €</div>
            <div className="text-[11px] text-slate-400">net à la clôture</div>
          </div>
        </div>
        <button className="w-full py-3 rounded-xl bg-orange-500 hover:bg-orange-600 text-white font-display font-bold text-sm flex items-center justify-center gap-2 transition-colors">
          <Camera className="w-4 h-4" /> Confirmer + photos → {proNet} €
        </button>
      </div>
    </Link>
  );
}

// ── Empty state ───────────────────────────────────────────────────
function EmptyState({ isPro }) {
  return (
    <div className="bg-white border border-dashed border-slate-300 rounded-xl p-10 text-center">
      <Inbox className="h-10 w-10 mx-auto text-slate-300" />
      <div className="font-display font-bold text-xl mt-3 text-slate-900">
        {isPro ? "Aucune mission ouverte" : "Aucune demande publiée"}
      </div>
      <p className="text-sm text-slate-600 mt-1 max-w-md mx-auto">
        {isPro ? "Revenez dans quelques instants ou ajustez vos filtres." : "Publiez votre première demande de dépannage en 2 minutes."}
      </p>
      {!isPro && (
        <Link to="/requests/new">
          <Button className="mt-4 bg-orange-600 hover:bg-orange-700 text-white">
            <PlusCircle className="mr-2 h-4 w-4" />Publier maintenant
          </Button>
        </Link>
      )}
    </div>
  );
}

// ── Mission en cours card (pro) ───────────────────────────────────
function MissionEnCoursCard({ req }) {
  const acceptedOffer = req.offers?.find(o => o.status === "accepted");
  const amount = acceptedOffer?.price_eur || 0;
  const fee    = Math.round(amount * 0.10);
  const proNet = amount - fee;
  const phase  = missionPhase(req);
  const pct    = phase === 0 ? 33 : phase === 1 ? 66 : 100;
  const phaseLabel = phase === 0 ? "Prise en charge" : phase === 1 ? "En transit" : "Livré";

  const statusCfg = {
    accepted:    { label: "ACCEPTÉE",   cls: "text-blue-600 bg-blue-50 border-blue-200" },
    in_progress: { label: "EN COURS",   cls: "text-orange-600 bg-orange-50 border-orange-200" },
  }[req.status] || { label: req.status, cls: "text-slate-500 bg-slate-100 border-slate-200" };

  return (
    <Link to={`/requests/${req.request_id}`} className="block">
      <div className="bg-white border border-slate-200 hover:border-orange-300 rounded-xl p-4 transition-all hover:shadow-sm">
        <div className="flex items-start justify-between gap-3 mb-3">
          <div className="min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <span className={`text-[11px] font-bold px-2 py-0.5 rounded-full border ${statusCfg.cls}`}>
                {statusCfg.label}
              </span>
              <span className="text-[11px] text-slate-400">{req.request_id?.slice(-5).toUpperCase()}</span>
            </div>
            <div className="font-display font-bold text-slate-900 flex items-center gap-2">
              {req.pickup?.city} <ArrowRight className="w-4 h-4 text-orange-500 flex-shrink-0" /> {req.dropoff?.city}
            </div>
            <div className="text-xs text-slate-500 mt-0.5">
              {Math.round(req.distance_km || 0)} km · {req.vehicle_type} · {req.client_name}
            </div>
          </div>
          <div className="text-right flex-shrink-0">
            <div className="font-display font-extrabold text-xl text-slate-900">{proNet} €</div>
            <div className="text-[11px] text-slate-400">net pro</div>
          </div>
        </div>
        {req.status === "in_progress" && (
          <div>
            <div className="flex items-center justify-between mb-1">
              <span className="text-[11px] text-slate-500">{phaseLabel}</span>
              <span className="text-[11px] font-bold text-orange-500">{pct}%</span>
            </div>
            <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
              <div className="h-full bg-orange-500 rounded-full transition-all duration-700" style={{ width: `${pct}%` }} />
            </div>
          </div>
        )}
        {req.status === "accepted" && (
          <div className="flex items-center gap-2 text-xs text-orange-600 font-semibold mt-1">
            <span className="w-1.5 h-1.5 rounded-full bg-orange-500 animate-pulse" />
            Action requise — confirmer la prise en charge
          </div>
        )}
      </div>
    </Link>
  );
}

// ── Dashboard ─────────────────────────────────────────────────────
export default function Dashboard() {
  const { user } = useAuth();
  const isPro    = user?.role === "pro";

  const [requests,       setRequests]       = useState([]);
  const [activeRequests, setActiveRequests] = useState([]);
  const [stats,          setStats]          = useState(null);
  const [filters,        setFilters]        = useState({ country: "all", urgency: "all" });
  const [priceFloor,     setPriceFloor]     = useState("");
  const [loading,        setLoading]        = useState(true);
  const [retourDismiss,  setRetourDismiss]  = useState(false);
  const [proTab,         setProTab]         = useState("marche");

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      try {
        const scope  = isPro ? "open" : "mine";
        const params = { scope };
        if (filters.country !== "all") params.country = filters.country;
        if (filters.urgency !== "all") params.urgency = filters.urgency;
        const { data } = await api.get("/requests", { params });
        if (!cancelled) setRequests(data);

        if (isPro) {
          // Missions du pro où son offre est acceptée / en cours
          const mine = await api.get("/requests", { params: { scope: "mine" } });
          if (!cancelled) setActiveRequests(
            mine.data.filter(r => r.status === "accepted" || r.status === "in_progress")
          );
          const s = await api.get("/stats/pro");
          if (!cancelled) setStats(s.data);
        }
      } catch (e) { console.error(e); }
      finally { if (!cancelled) setLoading(false); }
    }
    load();
    return () => { cancelled = true; };
  }, [isPro, filters.country, filters.urgency]);

  // ── Vue CLIENT ─────────────────────────────────────────────────
  if (!isPro) {
    const activeReqs    = requests.filter(r => r.status === "in_progress");
    const publishedReqs = requests.filter(r =>
      r.status === "open" || r.status === "offered" || r.status === "accepted" || r.status === "completed"
    );

    return (
      <div className="min-h-screen bg-slate-50" data-testid="dashboard-page">
        <Header />
        <div className="flex pt-16">
          <Sidebar />
          <main className="flex-1 p-4 sm:p-6 lg:p-8 max-w-3xl">
            <div className="flex items-start justify-between gap-4 mb-6">
              <div>
                <h1 className="font-display font-extrabold text-3xl text-slate-900 tracking-tight">Tableau de bord</h1>
                <p className="text-sm text-slate-500 mt-1">Bonjour, {user?.name?.split(" ")[0] || ""}</p>
              </div>
              <Link to="/requests/new" data-testid="new-request-link">
                <Button className="bg-orange-600 hover:bg-orange-700 text-white font-display font-bold">
                  <PlusCircle className="mr-2 h-4 w-4" /> Nouvelle demande
                </Button>
              </Link>
            </div>

            {loading ? <div className="py-16 text-center text-slate-400">Chargement…</div> : (
              <>
                {activeReqs.length > 0 && (
                  <div className="space-y-3 mb-6">
                    {activeReqs.map(r => <SequestCard key={r.request_id} req={r} />)}
                  </div>
                )}
                {publishedReqs.length > 0 ? (
                  <div className="bg-white border border-slate-200 rounded-xl px-5 py-4">
                    <div className="font-display font-bold text-base text-slate-900 mb-3">Missions publiées</div>
                    <div className="divide-y divide-slate-100">
                      {publishedReqs.map(r => <MissionRow key={r.request_id} req={r} />)}
                    </div>
                  </div>
                ) : activeReqs.length === 0 && <EmptyState isPro={false} />}
              </>
            )}
          </main>
        </div>
      </div>
    );
  }

  // ── Vue PRO ────────────────────────────────────────────────────
  const kmSaved  = Math.round((stats?.accepted_offers || 0) * 320);
  const thisMonth = new Date().toLocaleString("fr-FR", { month: "long", year: "numeric" });

  return (
    <div className="min-h-screen bg-slate-50" data-testid="dashboard-page">
      <Header />
      <div className="flex pt-16">
        <Sidebar />
        <main className="flex-1 p-4 sm:p-6 lg:p-8 max-w-2xl">

          {/* Titre */}
          <div className="mb-5">
            <h1 className="font-display font-extrabold text-3xl text-slate-900 tracking-tight">Tableau de bord</h1>
            <div className="flex items-center gap-2 mt-1 flex-wrap">
              <span className="text-sm text-slate-500">{user?.name}</span>
              {stats?.rating_avg > 0 && (
                <span className="text-sm text-amber-500 font-bold flex items-center gap-1">
                  · {stats.rating_avg} <Star className="w-3 h-3 fill-amber-400 text-amber-400" />
                </span>
              )}
              {stats?.accepted_offers > 0 && (
                <span className="text-sm text-slate-400">· {stats.accepted_offers} missions</span>
              )}
            </div>
          </div>

          {/* KPIs 2×2 */}
          {stats && (
            <div className="grid grid-cols-2 gap-3 mb-4" data-testid="pro-stats">
              {[
                { label: "MISSIONS/MOIS", val: stats.accepted_offers, sub: "+3 vs mois dernier", Icon: RotateCcw, col: "text-orange-500" },
                { label: "REVENUS",        val: `${stats.revenue_eur.toFixed(0)} €`, sub: thisMonth, Icon: DollarSign, col: "text-emerald-500" },
                { label: "KM ÉCONOMISÉS",  val: kmSaved.toLocaleString("fr-FR"), sub: "Retours", Icon: TrendingUp, col: "text-blue-500" },
                { label: "NOTE MOYENNE",   val: stats.rating_avg ? `${stats.rating_avg}` : "—", sub: `${stats.rating_count || 0} avis`, Icon: Star, col: "text-amber-400" },
              ].map(({ label, val, sub, Icon, col }) => (
                <div key={label} className="bg-white border border-slate-200 rounded-xl p-4">
                  <div className="flex items-start justify-between mb-2">
                    <div className="text-[10px] uppercase tracking-widest text-slate-400 font-bold">{label}</div>
                    <div className="w-7 h-7 rounded-lg bg-slate-50 flex items-center justify-center">
                      <Icon className={`w-3.5 h-3.5 ${col}`} />
                    </div>
                  </div>
                  <div className="font-display font-extrabold text-2xl text-slate-900">{val}</div>
                  <div className="text-xs text-slate-400 mt-1">{sub}</div>
                </div>
              ))}
            </div>
          )}

          {/* Onglets */}
          <div className="flex gap-1 bg-slate-100 rounded-xl p-1 mb-4">
            <button
              onClick={() => setProTab("marche")}
              className={`flex-1 py-2 px-3 rounded-lg text-sm font-display font-bold transition-all ${
                proTab === "marche"
                  ? "bg-white text-slate-900 shadow-sm"
                  : "text-slate-500 hover:text-slate-700"
              }`}
            >
              Marché ouvert
            </button>
            <button
              onClick={() => setProTab("missions")}
              className={`flex-1 py-2 px-3 rounded-lg text-sm font-display font-bold transition-all flex items-center justify-center gap-2 ${
                proTab === "missions"
                  ? "bg-white text-slate-900 shadow-sm"
                  : "text-slate-500 hover:text-slate-700"
              }`}
            >
              Mes missions en cours
              {activeRequests.length > 0 && (
                <span className={`text-[11px] font-bold px-1.5 py-0.5 rounded-full ${
                  proTab === "missions" ? "bg-orange-100 text-orange-600" : "bg-orange-500 text-white"
                }`}>
                  {activeRequests.length}
                </span>
              )}
            </button>
          </div>

          {/* Contenu onglet Marché ouvert */}
          {proTab === "marche" && (
            <>
              {/* Plancher tarifaire */}
              <div className="bg-white border border-slate-200 rounded-xl p-4 mb-3">
                <div className="flex items-start justify-between gap-4">
                  <div className="min-w-0">
                    <div className="font-display font-bold text-sm text-slate-900 mb-0.5 flex items-center gap-2">
                      <span className="w-2 h-2 rounded-full bg-orange-400" />
                      Plancher tarifaire
                    </div>
                    <div className="text-xs text-slate-400">Missions sous ce seuil sont signalées</div>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <input
                      type="number"
                      value={priceFloor}
                      onChange={e => setPriceFloor(e.target.value)}
                      placeholder="Ex: 250"
                      className="w-24 h-9 px-3 text-sm font-bold text-right border border-slate-200 rounded-lg outline-none focus:border-orange-400 bg-slate-50"
                    />
                    <span className="text-sm font-bold text-slate-600">€ min</span>
                  </div>
                </div>
              </div>

              {/* Notif retour disponible */}
              {!retourDismiss && requests.length > 0 && (
                <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 mb-3 relative">
                  <button onClick={() => setRetourDismiss(true)} className="absolute top-3 right-3 text-slate-400 hover:text-slate-600">
                    <X className="w-4 h-4" />
                  </button>
                  <div className="flex items-start gap-3">
                    <span className="text-lg mt-0.5">🔄</span>
                    <div className="flex-1 min-w-0">
                      <div className="font-display font-bold text-sm text-emerald-800 mb-1">
                        Retour disponible depuis {requests[0]?.dropoff?.city} !
                      </div>
                      <div className="text-xs text-emerald-600 mb-3">
                        Une mission compatible vient d'être publiée depuis votre ville de livraison.{" "}
                        <span className="font-semibold">
                          {requests[0]?.pickup?.city} → {requests[0]?.dropoff?.city} · {Math.round(requests[0]?.distance_km || 0)} km · {requests[0]?.budget_eur || "—"} €
                        </span>
                      </div>
                      <Link to="/matches">
                        <button className="flex items-center gap-2 px-3 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-xs font-bold transition-colors">
                          <RotateCcw className="w-3.5 h-3.5" /> Voir les missions retour
                        </button>
                      </Link>
                    </div>
                  </div>
                </div>
              )}

              {/* Optimisation retour */}
              {kmSaved > 0 && (
                <div className="bg-slate-900 rounded-xl p-4 flex items-center justify-between gap-4 mb-3">
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-lg bg-orange-500/20 flex items-center justify-center flex-shrink-0">
                      <RotateCcw className="w-4 h-4 text-orange-400" />
                    </div>
                    <div>
                      <div className="font-display font-bold text-white text-sm">Optimisation retour ce mois</div>
                      <div className="text-xs text-slate-400 mt-0.5">
                        Évitez {kmSaved.toLocaleString("fr-FR")} km à vide — générez +{Math.round(kmSaved * 0.44)} €.
                      </div>
                    </div>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <div className="font-display font-extrabold text-orange-400 text-xl">+{Math.round(kmSaved * 0.44)} €</div>
                    <div className="text-[10px] text-slate-500">missions retour</div>
                  </div>
                </div>
              )}

              {/* Filtres + grille */}
              <div className="flex flex-wrap items-center gap-2 mb-4 p-3 bg-white border border-slate-200 rounded-xl">
                <Filter className="h-4 w-4 text-slate-400 ml-1" />
                <Select value={filters.country} onValueChange={v => setFilters(f => ({ ...f, country: v }))}>
                  <SelectTrigger className="w-36 h-8 text-xs" data-testid="filter-country"><SelectValue placeholder="Pays" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tous les pays</SelectItem>
                    <SelectItem value="FR">🇫🇷 France</SelectItem>
                    <SelectItem value="BE">🇧🇪 Belgique</SelectItem>
                    <SelectItem value="ES">🇪🇸 Espagne</SelectItem>
                    <SelectItem value="DE">🇩🇪 Allemagne</SelectItem>
                  </SelectContent>
                </Select>
                <Select value={filters.urgency} onValueChange={v => setFilters(f => ({ ...f, urgency: v }))}>
                  <SelectTrigger className="w-36 h-8 text-xs" data-testid="filter-urgency"><SelectValue placeholder="Urgence" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Toutes urgences</SelectItem>
                    <SelectItem value="emergency">Urgence</SelectItem>
                    <SelectItem value="high">Haute</SelectItem>
                    <SelectItem value="medium">Moyenne</SelectItem>
                    <SelectItem value="low">Basse</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {loading ? (
                <div className="py-12 text-center text-slate-400">Chargement des missions…</div>
              ) : requests.length === 0 ? (
                <EmptyState isPro />
              ) : (
                <div className="grid sm:grid-cols-2 gap-4" data-testid="requests-grid">
                  {requests.map(r => <RequestCard key={r.request_id} req={r} currentProId={user?.user_id} />)}
                </div>
              )}
            </>
          )}

          {/* Contenu onglet Mes missions en cours */}
          {proTab === "missions" && (
            <div>
              {loading ? (
                <div className="py-12 text-center text-slate-400">Chargement…</div>
              ) : activeRequests.length === 0 ? (
                <div className="bg-white border border-dashed border-slate-300 rounded-xl p-10 text-center">
                  <CheckCircle2 className="h-10 w-10 mx-auto text-slate-300" />
                  <div className="font-display font-bold text-xl mt-3 text-slate-900">Aucune mission en cours</div>
                  <p className="text-sm text-slate-500 mt-1">Les missions que vous acceptez apparaîtront ici.</p>
                  <button
                    onClick={() => setProTab("marche")}
                    className="mt-4 inline-flex items-center gap-2 px-4 py-2 bg-orange-600 hover:bg-orange-700 text-white rounded-lg text-sm font-display font-bold transition-colors"
                  >
                    <ArrowRight className="w-4 h-4" /> Voir le marché ouvert
                  </button>
                </div>
              ) : (
                <div className="space-y-3">
                  {activeRequests.map(r => <MissionEnCoursCard key={r.request_id} req={r} />)}
                </div>
              )}
            </div>
          )}

        </main>
      </div>
    </div>
  );
}
